import { Injectable } from '@angular/core';
import { TableColumnProvider } from '../../../interfaces/table-column-provider';
import { TableColumn } from '../../../models/table-column';
import { TableColumnService } from '../services/table-column.service';
import { CountriesService } from '../services/countries.service';
import { Contact } from '../../../models/contact';
import { ContactColumnEnum } from '../../../enum/contact-column.enum';
import { EntityEnum } from '../../../enum/entity.enum';
import { Store } from '@ngrx/store';
import { AppState } from '../../../store';
import { contactEditorEdit } from '../../../store/contact-editor/contact-editor.actions';

@Injectable({
    providedIn: 'root'
})
export class ContactColumnsService implements TableColumnProvider {

    constructor(private tableColumnService: TableColumnService, private countriesService: CountriesService,
                private store: Store<AppState>) {
        this.tableColumnService.registerProvider(this);
    }

    get(id: string): TableColumn {
        switch (id) {
            case ContactColumnEnum.CONTACT_ATTN:
                return {
                    id: ContactColumnEnum.CONTACT_ATTN,
                    caption: $localize`Attn`,
                    width: 200,
                    sort: 'attn',
                    render: (contact: Contact) => contact.card.attn
                };
            case ContactColumnEnum.CONTACT_ORGANIZATION:
                return {
                    id: ContactColumnEnum.CONTACT_ORGANIZATION,
                    caption: $localize`Company`,
                    width: 200,
                    sort: 'organization',
                    render: (contact: Contact) => contact.card.organization,
                    button: (contact: Contact) => this.store.dispatch(contactEditorEdit({ id: contact.id }))
                };
            case ContactColumnEnum.CONTACT_STREET1:
                return {
                    id: ContactColumnEnum.CONTACT_STREET1,
                    caption: $localize`Street #1`,
                    width: 150,
                    sort: 'street1',
                    render: (contact: Contact) => contact.card.street1
                };
            case ContactColumnEnum.CONTACT_STREET2:
                return {
                    id: ContactColumnEnum.CONTACT_STREET2,
                    caption: $localize`Street #2`,
                    width: 150,
                    sort: 'street2',
                    render: (contact: Contact) => contact.card.street2
                };
            case ContactColumnEnum.CONTACT_STREET3:
                return {
                    id: ContactColumnEnum.CONTACT_STREET3,
                    caption: $localize`Street #3`,
                    width: 150,
                    render: (contact: Contact) => contact.card.street3
                };
            case ContactColumnEnum.CONTACT_CITY:
                return {
                    id: ContactColumnEnum.CONTACT_CITY,
                    caption: $localize`City`,
                    width: 150,
                    sort: 'city',
                    render: (contact: Contact) => contact.card.city
                };
            case ContactColumnEnum.CONTACT_PROVINCE:
                return {
                    id: ContactColumnEnum.CONTACT_PROVINCE,
                    caption: $localize`Province/State`,
                    width: 100,
                    sort: 'province',
                    render: (contact: Contact) => this.countriesService.provinceName(contact.card.country, contact.card.province)
                };
            case ContactColumnEnum.CONTACT_COUNTRY:
                return {
                    id: ContactColumnEnum.CONTACT_COUNTRY,
                    caption: $localize`Country`,
                    width: 100,
                    sort: 'country',
                    render: (contact: Contact) => this.countriesService.countryName(contact.card.country)
                };
            case ContactColumnEnum.CONTACT_POSTALCODE:
                return {
                    id: ContactColumnEnum.CONTACT_POSTALCODE,
                    caption: $localize`Postal Code/ZIP`,
                    width: 100,
                    sort: 'postalcode',
                    render: (contact: Contact) => contact.card.postalCode
                };
            case ContactColumnEnum.CONTACT_TEL:
                return {
                    id: ContactColumnEnum.CONTACT_TEL,
                    caption: $localize`Telephone`,
                    width: 100,
                    sort: 'telephone',
                    render: (contact: Contact) => contact.card.telephone
                };
            case ContactColumnEnum.CONTACT_EMAIL:
                return {
                    id: ContactColumnEnum.CONTACT_EMAIL,
                    caption: $localize`Email`,
                    width: 100,
                    sort: 'email',
                    render: (contact: Contact) => contact.card.email
                };
            case ContactColumnEnum.CONTACT_ADDRESS_BOOK:
                return {
                    id: ContactColumnEnum.CONTACT_ADDRESS_BOOK,
                    caption: $localize`Address Book`,
                    width: 100,
                    sort: 'addressBook',
                    render: (contact: Contact) => contact.addressBookName
                };
            case ContactColumnEnum.CONTACT_REFERENCE:
                return {
                    id: ContactColumnEnum.CONTACT_REFERENCE,
                    caption: $localize`Reference`,
                    width: 150,
                    sort: 'reference',
                    render: (contact: Contact) => contact.reference
                };
        }
    }

    ids(): string[] {
        return [
            ContactColumnEnum.CONTACT_ATTN,
            ContactColumnEnum.CONTACT_ORGANIZATION,
            ContactColumnEnum.CONTACT_STREET1,
            ContactColumnEnum.CONTACT_STREET2,
            ContactColumnEnum.CONTACT_STREET3,
            ContactColumnEnum.CONTACT_CITY,
            ContactColumnEnum.CONTACT_PROVINCE,
            ContactColumnEnum.CONTACT_COUNTRY,
            ContactColumnEnum.CONTACT_POSTALCODE,
            ContactColumnEnum.CONTACT_TEL,
            ContactColumnEnum.CONTACT_EMAIL,
            ContactColumnEnum.CONTACT_ADDRESS_BOOK,
            ContactColumnEnum.CONTACT_REFERENCE
        ];
    }

    supports(entityClass: string): boolean {
        return entityClass === EntityEnum.CONTACT;
    }
}
